/**
 * UnifiedSearchBar - Context-aware search bar in the header
 *
 * Replaces GlobalSearch.jsx. Always-visible input in the header that:
 * 1. Writes searchQuery to SearchContext → current view filters in real-time
 * 2. Shows cross-entity results in a dropdown (excludes current view's entity type)
 * 3. Ctrl+K focuses the input
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Car, User, MapPin, Building, X, ArrowRight, Wrench, AlertTriangle, FileText, Shield } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../api.js';
import { useSearch } from '../../context/SearchContext.jsx';
import { useTranslation } from 'react-i18next';
import { useProfile } from '../../hooks/useProfile.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { isViewAllowed } from '../../utils/viewAccess.js';

// Search result type → the view a click would navigate to (mirrors handleResultClick).
// A profile that cannot reach that view must not be OFFERED the result either —
// a hidden menu entry is not a permission (H2, project memory bug_driver_no_role_guard_p0.md).
const RESULT_TYPE_VIEW = {
  vehicle: 'vehicles',
  driver: 'drivers',
  site: 'settings',
  organization: 'settings',
  maintenance: 'maintenance',
  claim: 'claims',
  document: 'documents',
  insurance: 'insurance'
};

// Icon mapping for result types (reused from GlobalSearch)
const TYPE_ICONS = {
  vehicle: Car,
  driver: User,
  site: MapPin,
  organization: Building,
  maintenance: Wrench,
  claim: AlertTriangle,
  document: FileText,
  insurance: Shield
};

// Color mapping for result types
const TYPE_COLORS = {
  vehicle: { bg: 'bg-blue-100', text: 'text-blue-600' },
  driver: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  site: { bg: 'bg-purple-100', text: 'text-purple-600' },
  organization: { bg: 'bg-amber-100', text: 'text-amber-600' },
  maintenance: { bg: 'bg-orange-100', text: 'text-orange-600' },
  claim: { bg: 'bg-red-100', text: 'text-red-600' },
  document: { bg: 'bg-gray-100', text: 'text-gray-600' },
  insurance: { bg: 'bg-cyan-100', text: 'text-cyan-600' }
};

// Category labels live in locales: search.categories.<type>

export function UnifiedSearchBar({ onNavigate }) {
  const { t } = useTranslation();
  const { searchQuery, setSearchQuery, clearSearch, viewConfig, currentView } = useSearch();
  const { profile } = useProfile();
  const { isSuperuser, user, hasFeature } = useAuth();
  const isAdmin = user?.role === 'admin' || isSuperuser;
  const isTypeSearchAllowed = useCallback((type) => {
    const view = RESULT_TYPE_VIEW[type];
    return view ? isViewAllowed(view, profile, { isAdmin, isSuperuser, hasFeature }) : true;
  }, [profile, isAdmin, isSuperuser, hasFeature]);
  // Placeholder: translated per view, SearchContext (French reference) as fallback
  const placeholder = currentView
    ? t(`search.placeholders.${currentView}`, { defaultValue: viewConfig.placeholder })
    : t('search.placeholders.default', { defaultValue: viewConfig.placeholder });
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Fetch searchable data (same queries as GlobalSearch, shared via React Query cache)
  const { data: vehicles = [] } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => (await api.vehicle.getAll()).data || [],
    staleTime: 60000
  });

  const { data: drivers = [] } = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => (await api.driver.getAll()).data || [],
    staleTime: 60000
  });

  const { data: sites = [] } = useQuery({
    queryKey: ['workingSites'],
    queryFn: async () => (await api.workingSite.getAll()).data || [],
    staleTime: 60000
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => (await api.organization.getAll()).data || [],
    staleTime: 60000
  });

  const { data: maintenance = [] } = useQuery({
    queryKey: ['maintenance'],
    queryFn: async () => (await api.maintenanceRequest.getAll()).data || [],
    staleTime: 60000
  });

  const { data: claims = [] } = useQuery({
    queryKey: ['claims'],
    queryFn: async () => (await api.claim.getAll()).data || [],
    staleTime: 60000
  });

  // Cross-entity search — excludes current view's entity type
  const crossEntityResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];

    const q = searchQuery.toLowerCase().trim();
    const maxPerCategory = 3;
    const results = [];
    const excludeType = viewConfig.entityType;

    // Search vehicles
    if (excludeType !== 'vehicle' && isTypeSearchAllowed('vehicle')) {
      vehicles.filter(v => {
        const fields = [v.license_plate, v.vin, v.make, v.model, `${v.make} ${v.model}`, v.internal_id]
          .filter(Boolean).map(f => f.toLowerCase());
        return fields.some(f => f.includes(q));
      }).slice(0, maxPerCategory).forEach(v => results.push({
        type: 'vehicle', id: v.id,
        primary: v.license_plate || `${v.make} ${v.model}`,
        secondary: `${v.make} ${v.model} ${v.year || ''}`.trim(),
        badge: v.status
      }));
    }

    // Search drivers
    if (excludeType !== 'driver' && isTypeSearchAllowed('driver')) {
      drivers.filter(d => {
        const fields = [d.first_name, d.last_name, `${d.first_name} ${d.last_name}`, d.email, d.phone, d.license_number]
          .filter(Boolean).map(f => f.toLowerCase());
        return fields.some(f => f.includes(q));
      }).slice(0, maxPerCategory).forEach(d => results.push({
        type: 'driver', id: d.id,
        primary: `${d.first_name} ${d.last_name}`,
        secondary: d.email || d.phone || '',
        badge: d.status
      }));
    }

    // Search sites
    if (excludeType !== 'site' && isTypeSearchAllowed('site')) {
      sites.filter(s => {
        const fields = [s.site_name, s.address, s.city, s.code]
          .filter(Boolean).map(f => f.toLowerCase());
        return fields.some(f => f.includes(q));
      }).slice(0, maxPerCategory).forEach(s => results.push({
        type: 'site', id: s.id,
        primary: s.site_name,
        secondary: s.city || s.address || ''
      }));
    }

    // Search organizations
    if (excludeType !== 'organization' && isTypeSearchAllowed('organization')) {
      organizations.filter(o => {
        const fields = [o.name, o.code, o.legal_name]
          .filter(Boolean).map(f => f.toLowerCase());
        return fields.some(f => f.includes(q));
      }).slice(0, maxPerCategory).forEach(o => results.push({
        type: 'organization', id: o.id,
        primary: o.name,
        secondary: o.code || ''
      }));
    }

    // Search maintenance
    if (excludeType !== 'maintenance' && isTypeSearchAllowed('maintenance')) {
      maintenance.filter(m => {
        const fields = [m.title, m.description, m.vehicle_license_plate, String(m.id)]
          .filter(Boolean).map(f => f.toLowerCase());
        return fields.some(f => f.includes(q));
      }).slice(0, maxPerCategory).forEach(m => results.push({
        type: 'maintenance', id: m.id,
        primary: m.title || t('search.fallback.maintenance', { id: m.id }),
        secondary: m.vehicle_license_plate || '',
        badge: m.status
      }));
    }

    // Search claims
    if (excludeType !== 'claim' && isTypeSearchAllowed('claim')) {
      claims.filter(c => {
        const fields = [c.reference, c.description, c.vehicle_license_plate, String(c.id)]
          .filter(Boolean).map(f => f.toLowerCase());
        return fields.some(f => f.includes(q));
      }).slice(0, maxPerCategory).forEach(c => results.push({
        type: 'claim', id: c.id,
        primary: c.reference || t('search.fallback.claim', { id: c.id }),
        secondary: c.vehicle_license_plate || '',
        badge: c.status
      }));
    }

    return results;
  }, [searchQuery, viewConfig.entityType, vehicles, drivers, sites, organizations, maintenance, claims, t, isTypeSearchAllowed]);

  const hasResults = crossEntityResults.length > 0;

  // Group results by type
  const groupedResults = useMemo(() => {
    return crossEntityResults.reduce((acc, r) => {
      if (!acc[r.type]) acc[r.type] = [];
      acc[r.type].push(r);
      return acc;
    }, {});
  }, [crossEntityResults]);

  // Ctrl+K focuses input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        clearSearch();
        setShowDropdown(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [clearSearch]);

  // Keyboard navigation in dropdown
  useEffect(() => {
    const handleKeyNav = (e) => {
      if (!showDropdown || !hasResults) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, crossEntityResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && crossEntityResults[selectedIndex]) {
        e.preventDefault();
        handleResultClick(crossEntityResults[selectedIndex]);
      }
    };
    document.addEventListener('keydown', handleKeyNav);
    return () => document.removeEventListener('keydown', handleKeyNav);
  }, [showDropdown, hasResults, crossEntityResults, selectedIndex]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Show dropdown when there are cross-entity results and input is focused
  useEffect(() => {
    if (hasResults && searchQuery.length >= 2) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [hasResults, searchQuery]);

  // Click outside closes dropdown (but preserves query text for local filtering)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const handleResultClick = useCallback((result) => {
    const viewMap = {
      vehicle: 'vehicles',
      driver: 'drivers',
      site: 'settings',
      organization: 'settings',
      maintenance: 'maintenance',
      claim: 'claims',
      document: 'documents',
      insurance: 'insurance'
    };
    const viewName = viewMap[result.type] || 'dashboard';
    onNavigate?.(viewName, { selectedId: result.id, type: result.type });
    clearSearch();
    setShowDropdown(false);
  }, [onNavigate, clearSearch]);

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-600';
    switch (status.toLowerCase()) {
      case 'active': return 'bg-emerald-100 text-emerald-600';
      case 'maintenance': return 'bg-orange-100 text-orange-600';
      case 'inactive': case 'out_of_service': return 'bg-red-100 text-red-600';
      case 'completed': case 'settled': return 'bg-emerald-100 text-emerald-600';
      case 'pending': case 'submitted': return 'bg-amber-100 text-amber-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFocus = () => {
    if (hasResults && searchQuery.length >= 2) {
      setShowDropdown(true);
    }
  };

  const handleClear = () => {
    clearSearch();
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative flex-1 max-w-xl" data-tour="global-search">
      {/* Always-visible search input */}
      <div className="relative flex items-center">
        <Search size={16} className="absolute left-3 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="w-full pl-9 pr-20 py-1.5 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
        />
        <div className="absolute right-2 flex items-center gap-1.5">
          {searchQuery && (
            <button
              onClick={handleClear}
              className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
            >
              <X size={14} />
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-[10px] text-gray-500 dark:text-slate-400 font-medium">
            {t('search.shortcut')}
          </kbd>
        </div>
      </div>

      {/* Cross-entity results dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="max-h-[50vh] overflow-y-auto">
            {Object.entries(groupedResults).map(([type, items]) => {
              const colors = TYPE_COLORS[type] || TYPE_COLORS.vehicle;
              const Icon = TYPE_ICONS[type] || Search;

              return (
                <div key={type} className="mb-0.5">
                  <p className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase bg-gray-50 dark:bg-slate-900/50">
                    {t(`search.categories.${type}`, { defaultValue: type })} ({items.length})
                  </p>
                  {items.map((result) => {
                    const globalIdx = crossEntityResults.indexOf(result);
                    const isSelected = globalIdx === selectedIndex;

                    return (
                      <button
                        key={`${type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 text-left transition-colors
                          ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'}
                        `}
                      >
                        <div className={`p-1.5 rounded-lg ${colors.bg}`}>
                          <Icon size={12} className={colors.text} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {result.primary}
                          </p>
                          {result.secondary && (
                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                              {result.secondary}
                            </p>
                          )}
                        </div>
                        {result.badge && (
                          <span className={`px-1.5 py-0.5 text-xs rounded ${getStatusColor(result.badge)}`}>
                            {result.badge}
                          </span>
                        )}
                        <ArrowRight size={12} className="text-gray-400 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-3 py-1.5 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
            <div className="flex gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-gray-600 dark:text-slate-300">↑↓</kbd>
                {t('common.actions.navigate')}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-gray-600 dark:text-slate-300">Enter</kbd>
                {t('common.actions.open')}
              </span>
            </div>
            <span>{t('common.pagination.results', { count: crossEntityResults.length })}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default UnifiedSearchBar;
